import { CdkDragEnd, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { Component, ViewContainerRef, inject } from '@angular/core';
import {
  MatDialogContainer,
  DialogPosition,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { AppConfig } from '@kildenconfig/app.config';
import { DialogType } from '@kildencore/services/dialog.service';
import { ObjectinfoService } from '@kildencore/services/objectinfo.service';
import { Observable } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgComponentOutlet } from '@angular/common';

@Component({
  selector: 'kilden3-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.css'],
  imports: [
    CdkDrag,
    CdkDragHandle,
    MatIcon,
    MatIconButton,
    CdkScrollable,
    MatDialogContent,
    NgComponentOutlet,
    MatDialogActions,
    MatButton,
    MatDialogClose,
  ],
})
export class Kilden3DialogComponent {
  data = inject(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject<MatDialogRef<Kilden3DialogComponent>>(MatDialogRef);
  private readonly _objectinfoService = inject(ObjectinfoService);
  private readonly _viewContainerRef = inject(ViewContainerRef);

  /**
   * Expose method for use when typed as DialogType
   * @see DialogType
   */
  afterClosed(): Observable<void> {
    return this._dialogRef.afterClosed();
  }

  /**
   * Expose method for use when typed as DialogType
   * @see DialogType
   */
  afterOpened(): Observable<void> {
    return this._dialogRef.afterOpened();
  }

  beforeClosed(): Observable<void> {
    return this._dialogRef.beforeClosed();
  }

  /**
   * Expose method for use when typed as DialogType
   * @see DialogType
   */
  close(): void {
    this._dialogRef.close();
  }

  closeDialog(): void {
    this._dialogRef.close();
  }

  getContainer(): MatDialogContainer | null {
    return this._dialogRef._containerInstance as MatDialogContainer;
  }

  getNativeElement(): HTMLElement {
    return this._viewContainerRef.element.nativeElement;
  }

  getSize(): { height?: number; width?: number } {
    const featureInfoOverlay = document.getElementsByClassName(AppConfig.ID_DIALOG_FEATURE_INFO);
    const containers = featureInfoOverlay[0].getElementsByClassName('.mat-mdc-dialog-surface');

    if (!containers[0]) {
      return { height: undefined, width: undefined };
    }

    const rect = containers[0].getBoundingClientRect();

    return { height: rect.height, width: rect.width };
  }

  getViewContainer(): ViewContainerRef {
    return this._viewContainerRef;
  }

  /**
   * Store the x,y position in objService
   */
  onDragEnded(event: CdkDragEnd): void {
    const { x, y } = event.source.getRootElement().getBoundingClientRect();
    this._objectinfoService.setStoredDialogPosition(x, y);
  }

  preventDrop(e: any) {
    e.preventDefault();
    e.dataTransfer.effectAllowed = 'none';
    e.dataTransfer.dropEffect = 'none';
  }

  /**
   * Expose method for use when typed as DialogType
   * @see DialogType
   */
  updatePosition(position?: DialogPosition): DialogType {
    this._dialogRef.updatePosition(position);
    return this;
  }
}
